/**
 * emailAyarService.js — Sistem Ayarları › E-posta sayfalarının, sistemin KENDİ gönderdiği postalarla ilgili çağrıları (backlog #573,
 * backend #566 / #567). Hesap listesi ve serbest şablonlar eskisi gibi MailSettingsContext / EmailTemplateContext'te kalır; buradakiler:
 *   · GET  /email/templates/system            → sistem şablonları (ACTIVATION, FORGOT_PASSWORD, ...): TR ve EN yan yana, açıklama, yer tutucular
 *   · PUT  /email/templates/{id}              → bir sistem şablonunun metni (SYSTEM_SETTINGS_MANAGE ister; 400: bilinmeyen / bozuk yer tutucu, boş alan)
 *   · GET  /email/templates/{id}/preview?languageId=  → gönderilecek posta, örnek değerlerle, sabit düzenin içinde { html, text, subject, language_id }
 *   · POST /email/templates/{id}/test?languageId=     → o postayı GİRİŞ YAPAN personelin KENDİ adresine yollar { status, sent, to, message, error, logId }
 *   · POST /email/system-emails/{id}/test             → bir gönderici hesabın çalıştığını aynı yolla dener
 *   · GET / POST /email/my-accounts, PUT / DELETE /email/my-accounts/{id}, POST /email/my-accounts/{id}/test → a staff member's OWN mail accounts (#574)
 *   · GET  /email/templates/{id}                      → bir şablonun tekil okuması: Türkçe aslı + yanında İngilizcesi (email_template_title_en / _content_en, yoksa null) (#583)
 *   · GET  /email/send/options, GET /email/templates, GET /email/users/search, POST /email/send → kayıtlı bir serbest şablonu seçilen kişilere göndermek (#584)
 * Hata mesajı backend'in `message` alanıdır (isteğin dilinde); yoksa HTTP / ağ hatası metni.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/email`;

export function emailHatasi(error, yedek) {
  const govde = error?.response?.data;
  const mesaj = govde && typeof govde === 'object' ? (govde.message || govde.error) : null;
  const e = new Error(mesaj || error?.message || yedek || 'Bilinmeyen hata');
  e.status = error?.response?.status ?? null;
  return e;
}

/** Bir şablonun tekil okuması (#583): liste yalnız Türkçeyi taşır, İngilizce yan metin bu okumayla gelir. 404: şablon yok. */
export async function getFreeTemplate(id) {
  try {
    const response = await axios.get(`${BASE}/templates/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

/**
 * Kendi e-posta hesaplarım (#574, backend /email/my-accounts): her personel yalnızca KENDİ hesaplarını görür / ekler / düzenler / siler / dener (en çok 3).
 * Hesap hep isteği yapanındır (gövdedeki user_id / is_default okunmaz); şifre yalnız yazılır; sunucu genel bir ALAN ADI olmalı (IP değil), port 25 / 465 / 587 / 2525.
 * Reddin metni backend'in `message` alanıdır (sınır, sunucu kuralı, şifre yeniden). Test: yalnızca isteği yapanın KENDİ adresine; saatte 10 (429).
 */
export async function listMyAccounts() {
  try {
    const response = await axios.get(`${BASE}/my-accounts`, { headers: getAuthHeader() });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw emailHatasi(error);
  }
}

export async function createMyAccount(form) {
  try {
    const response = await axios.post(`${BASE}/my-accounts`, form, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

export async function updateMyAccount(id, form) {
  try {
    const response = await axios.put(`${BASE}/my-accounts/${id}`, form, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

export async function deleteMyAccount(id) {
  try {
    const response = await axios.delete(`${BASE}/my-accounts/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

/** Hesabın çalıştığını kendi adresine bir posta atarak dener. { status SENT | BLOCKED | FAILED, sent, to, message, error, logId }; 429: saatte 10 test dolu. */
export async function testMyAccount(id) {
  try {
    const response = await axios.post(`${BASE}/my-accounts/${id}/test`, null, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

/**
 * Şablonu gönder (#584, backend /email/send): kayıtlı bir SERBEST şablon, seçilen kişilere olduğu gibi gider (konu = şablon başlığı, metin = şablon metni; istekte metin yoktur).
 * getSendOptions → { senders: [{ system_email_id, system_email_title, system_email_value, has_password, is_default, kind: OWN | DEFAULT | GENERAL }], max_recipients, mails_per_hour, remaining }:
 * kendi hesaplarınız; sistemin varsayılanı ve genel hesapları yalnızca Sistem Ayarları yetkisi olanlara gelir.
 */
export async function getSendOptions() {
  try {
    const response = await axios.get(`${BASE}/send/options`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

/**
 * Gönderim formunun listesi: serbest şablonlar VE sistemin kendi gönderdiği postalar. Her satırda email_template_id, email_template_title, has_en, template_scope (DENETMEN | SYSTEM).
 * Kapsam gönderilmez: backend ikisini de döner (2026-10-30; WAS (#584): yalnızca serbest şablonlar). Bir sistem postası (template_key ACTIVATION | FORGOT_PASSWORD) yalnızca KULLANICILARA gider: her biri için yeni bir şifre bağlantısı üretilir, gönderen sistemin varsayılanıdır, Kullanıcı Rolü Atama yetkisi ister (403); form ve liste formu onu işaretler, önizletir ve ona göre gönderir.
 * (Adı eski: listFreeTemplates.)
 */
export async function listFreeTemplates() {
  try {
    const response = await axios.get(`${BASE}/templates`, { params: { page: 1, limit: 200 }, headers: getAuthHeader() });
    return Array.isArray(response.data?.data) ? response.data.data : [];
  } catch (error) {
    throw emailHatasi(error);
  }
}

/** Alıcı arama: etkin kullanıcılar (ad, soyad, e-posta ya da giriş adı; en az 2 harf), en çok 20. */
export async function searchSendUsers(q) {
  try {
    const response = await axios.get(`${BASE}/users/search`, { params: { search: q }, headers: getAuthHeader() });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw emailHatasi(error);
  }
}

/**
 * Gönderir: { templateId, senderId, userIds, addresses, languageId } → { queued, skipped: [{ to, user_id, code, reason }], template_id, sender_id, sender }.
 * Postalar sıraya alınır (202; hiçbiri alınmadıysa 200); her birinin sonucu E-posta Kayıtları'ndadır. Reddin metni backend'in `message` alanıdır:
 * 400 alıcı yok / çok / sistem ya da boş şablon, 403 genel hesap yetkisiz, 404 şablon ya da hesap, 409 varsayılan gönderen yok, 429 saatlik sınır.
 */
export async function sendTemplateMail(payload) {
  try {
    const response = await axios.post(`${BASE}/send`, payload, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

/** Sistem şablonları, kayıt sırasıyla. Her biri: template_key, description, placeholders[{ name, token, description, allowed_in_subject }], email_template_* ve *_en. */
export async function listSystemTemplates() {
  try {
    const response = await axios.get(`${BASE}/templates/system`, { headers: getAuthHeader() });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw emailHatasi(error);
  }
}

/** Bir sistem şablonunun metnini yazar: { title, headline, content, button, titleEn, headlineEn, contentEn, buttonEn }; boş EN alanı o alanı temizler. */
export async function updateSystemTemplate(id, form) {
  try {
    const response = await axios.put(`${BASE}/templates/${id}`, form, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

/** Posta olarak önizleme (languageId 1 TR, 2 EN). `language_id` GERÇEKTEN kullanılan dildir: EN bitmemişse Türkçe döner. `render_error` doluysa html yoktur. */
export async function previewTemplate(id, languageId) {
  try {
    const response = await axios.get(`${BASE}/templates/${id}/preview`, { params: { languageId }, headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

/** "Bana test gönder": kendi adresine. { status SENT | BLOCKED | FAILED, sent, to, message, error, logId } — BLOCKED / FAILED de HTTP 200'dür. */
export async function sendTemplateTest(id, languageId) {
  try {
    const response = await axios.post(`${BASE}/templates/${id}/test`, null, { params: { languageId }, headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}

/** Bir gönderici hesaptan kendi adresine test postası (SYSTEM_SETTINGS_MANAGE ister). Aynı cevap şekli. */
export async function sendAccountTest(id) {
  try {
    const response = await axios.post(`${BASE}/system-emails/${id}/test`, null, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw emailHatasi(error);
  }
}
